import React, { memo, useMemo, useCallback, useRef } from 'react';
import { FormSelect } from '@core/components/form';
import { useObservable } from '@core/utils/hooks/rxjs';
import { useBloc } from '@core/utils/bloc';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { FlashMessage } from '@core/components/FlashMessage';
import { CommonService } from '@business/services';
import { PolicyWidgetForm, PolicyBlockVariation } from './PolicyBlock';
import { PolicyBlockBloc } from './PolicyBlockBloc';
import { R } from '@core/utils/r';
import { LibraryItemOptionResponse } from '@business/entities/library';

export interface EmbedBlockSettingsProps {
  form: PolicyWidgetForm;
  variation: PolicyBlockVariation;
}

export const PolicyBlockSettings = memo((props: EmbedBlockSettingsProps) => {
  const { form, variation } = props;
  const bloc = useBloc(PolicyBlockBloc, variation);
  const data = useObservable(bloc.items$);
  const loading = useLoadingState(bloc.loading);
  const items = useMemo(() => CommonService.selectItems(data || [], 'name', 'id'), [data]);
  // The policy whose content is currently in the block, restored if loading a new one fails.
  const appliedId = useRef(form.controls.id.value);

  const onChange = useCallback(
    (value: string) => {
      const policy = R.first(R.filter(data, (p: LibraryItemOptionResponse) => p.id === value)) as LibraryItemOptionResponse;
      if (!policy) return;

      // Not tied to this panel's lifetime: the block still needs its content if the panel is closed meanwhile.
      bloc.getContent(policy.id).subscribe({
        next: item => {
          if (form.controls.id.value !== value) return; // a newer policy was picked while this one loaded
          appliedId.current = value;
          try {
            const json = JSON.parse(item.content as string);
            form.patchValue({ text: json[0], heading: policy.name });
          } catch (e) {
            form.patchValue({ text: item.content as string, heading: policy.name });
          }
        },
        error: e => {
          if (form.controls.id.value !== value) return;
          form.patchValue({ id: appliedId.current });
          FlashMessage.error(e, `Could not load "${policy.name}"`, 'Please try again.');
        },
      });
    },
    [bloc, data, form],
  );

  const noOptionsMessage = loading.loading
    ? 'Loading policies...'
    : loading.error
    ? 'Could not load policies. Close and reopen this block to try again.'
    : undefined;

  return (
    <div>
      <FormSelect control={form.controls.id} items={items} onChange={onChange} noOptionsMessage={noOptionsMessage} />
    </div>
  );
});
